import React, { useState, useMemo } from 'react';
import { Box, Button, Input, Select, Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea, useDisclosure, Flex, Heading, Text, FormControl, FormLabel, IconButton, useColorModeValue } from '@chakra-ui/react';
import { FaTrash, FaEdit, FaPlus, FaAngleLeft, FaAngleRight } from 'react-icons/fa';

function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const PAGE_SIZE = 5;

function CreateProjectModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [env, setEnv] = useState('Test');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  function handleCreate() {
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    onCreate({ name: name.trim(), env: env.trim(), description: description.trim(), createdAt: new Date().toISOString() });
    setName(''); setEnv('Test'); setDescription(''); setError('');
  }
  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Create Project</ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={3}>
          <FormControl>
            <FormLabel>Project Name</FormLabel>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter project name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          <FormControl>
             <FormLabel>Environment</FormLabel>
            <Select value={env} onChange={e => setEnv(e.target.value)} bg={inputBg} borderColor={inputBorder}>
              <option value="Test">Test</option>
              <option value="Staging">Staging</option>
              <option value="Production">Production</option>
            </Select>
          </FormControl>
          <FormControl>
             <FormLabel>Description</FormLabel>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter project description" bg={inputBg} borderColor={inputBorder} />
          </FormControl>
          {error && <Box color="red.500" fontSize="sm">{error}</Box>}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button onClick={onClose} variant="ghost">Cancel</Button>
          <Button colorScheme="green" onClick={handleCreate}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsProjectsTab({ projects, setProjects, selectedProject, setSelectedProject }) {
  const [editIdx, setEditIdx] = useState(null);
  const [editData, setEditData] = useState({ name: '', env: '', description: '' });
  const { isOpen: isConfirmOpen, onOpen: onOpenConfirm, onClose: onCloseConfirm } = useDisclosure();
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure();

  // Chakra color mode values
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  // Filtered and paginated projects
  const filtered = useMemo(() => {
    if (!search) return projects;
    return projects.filter(p =>
      (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.env || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [projects, search]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)), [filtered]);
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  function startEdit(idx) {
    const globalIdx = (page - 1) * PAGE_SIZE + idx;
    setEditIdx(globalIdx);
    setEditData(projects[globalIdx]);
  }
  function saveEdit() {
    const updated = [...projects];
    updated[editIdx] = { ...editData };
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    setEditIdx(null);
  }
  function cancelEdit() {
    setEditIdx(null);
  }
  function confirmDelete(idx) {
    const globalIdx = (page - 1) * PAGE_SIZE + idx;
    setDeleteIdx(globalIdx);
    onOpenConfirm();
  }
  function doDelete() {
    const updated = projects.filter((_, i) => i !== deleteIdx);
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    if (projects[deleteIdx]?.name === selectedProject && updated.length > 0) {
      setSelectedProject(updated[0].name);
    } else if (projects[deleteIdx]?.name === selectedProject && updated.length === 0) {
       setSelectedProject(null); // Or handle empty state as appropriate
    }
    onCloseConfirm();
    setDeleteIdx(null);
  }
  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
  }
  function handlePageChange(newPage) {
    setPage(newPage);
  }
  function handleCreateProject(proj) {
    const updated = [...projects, proj];
    setProjects(updated);
    localStorage.setItem('projects', JSON.stringify(updated));
    onCloseCreate();
  }

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>Projects</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage your Playwright projects and environments.</Text>
      <Flex align="center" mb={6} gap={4} flexWrap="wrap">
        <Button leftIcon={<FaPlus />} colorScheme="green" onClick={() => onOpenCreate()} size="sm" borderRadius="lg">New Project</Button>
        <Input
          placeholder="Search projects..."
          value={search}
          onChange={handleSearch}
          size="sm"
          borderRadius="full"
          maxW="220px"
          minW="120px"
          bg={inputBg}
          borderColor={inputBorder}
          ml="auto"
        />
      </Flex>
      <CreateProjectModal open={isCreateOpen} onClose={onCloseCreate} onCreate={handleCreateProject} />

      <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={borderColor} bg={cardBg}>
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th color={tableHeaderColor} py={3}>Name</Th>
              <Th color={tableHeaderColor} py={3}>Environment</Th>
              <Th color={tableHeaderColor} py={3}>Description</Th>
              <Th color={tableHeaderColor} py={3}>Created</Th>
              <Th py={3}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {paged.map((p, i) => {
              const globalIdx = (page - 1) * PAGE_SIZE + i;
              const isEditing = editIdx === globalIdx;
              return (
                <Tr key={p.name || i} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Input value={editData.name} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} size="sm" bg={inputBg} borderColor={inputBorder} />
                    ) : p.name}
                  </Td>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Select value={editData.env} onChange={e => setEditData(d => ({ ...d, env: e.target.value }))} size="sm" bg={inputBg} borderColor={inputBorder}>
                        <option value="Test">Test</option>
                        <option value="Staging">Staging</option>
                        <option value="Production">Production</option>
                      </Select>
                    ) : p.env}
                  </Td>
                  <Td color={textColor}>
                    {isEditing ? (
                      <Input value={editData.description} onChange={e => setEditData(d => ({ ...d, description: e.target.value }))} size="sm" width={180} bg={inputBg} borderColor={inputBorder} />
                    ) : p.description}
                  </Td>
                  <Td color={textColor}>{formatDate(p.createdAt)}</Td>
                  <Td textAlign="right">
                    {isEditing ? (
                      <Flex gap={2} justify="flex-end">
                        <Button onClick={saveEdit} size="xs" colorScheme="green" borderRadius="lg">Save</Button>
                        <Button onClick={cancelEdit} size="xs" borderRadius="lg">Cancel</Button>
                      </Flex>
                    ) : (
                      <Flex gap={2} justify="flex-end">
                         <IconButton
                          aria-label="Edit project"
                          icon={<FaEdit />}
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          onClick={() => startEdit(i)}
                        />
                         <IconButton
                          aria-label="Delete project"
                          icon={<FaTrash />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => confirmDelete(i)}
                        />
                      </Flex>
                    )}
                  </Td>
                </Tr>
              );
            })}
             {paged.length === 0 && ( // Add empty state row
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">No projects found.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Flex justify="space-between" align="center" mt={4} gap={4} flexWrap="wrap">
        <Box>
          <Button
            onClick={() => handlePageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            size="sm"
            leftIcon={<FaAngleLeft />}
            borderRadius="lg"
          >Prev</Button>
          <Box as="span" px={3} fontWeight="semibold" color={textColor}>Page {page} of {totalPages}</Box>
           <Button
            onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            size="sm"
            rightIcon={<FaAngleRight />}
            borderRadius="lg"
          >Next</Button>
        </Box>
      </Flex>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onCloseConfirm}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Project</ModalHeader>
          <ModalBody>Are you sure you want to delete this project?</ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={doDelete}>Delete</Button>
            <Button variant="ghost" onClick={onCloseConfirm}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}